import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import QRScanner from './components/QRScanner';
import PINEntry from './components/PINEntry';
import AuthenticationStatus from './components/AuthenticationStatus';
import SecurityFeatures from './components/SecurityFeatures';
import { staffAuthService } from '../../lib/staffAuthService';

const StaffQRCodePINLogin = () => {
  const navigate = useNavigate();
  const [authStep, setAuthStep] = useState('qr'); // 'qr', 'pin', 'success', 'error'
  const [qrData, setQrData] = useState(null);
  const [staffInfo, setStaffInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const roleRedirectMap = {
    staff: '/staff-dashboard',
    trainer: '/trainer-dashboard',
    manager: '/analytics-dashboard'
  };

  const handleQRScan = async (scannedData) => {
    setLoading(true);
    setError(null);

    try {
      // Extract QR code identifier from scanned data
      // Format: STAFF_QR_{uuid}
      const qrIdentifier = scannedData?.replace('STAFF_QR_', '');

      // Verify QR code
      const { data, error: verifyError } = await staffAuthService?.verifyQRCode(qrIdentifier);

      if (verifyError || !data) {
        setError(verifyError?.message || 'Invalid QR code. Please try again.');
        setAuthStep('error');
        setLoading(false);
        return;
      }

      // QR verified, move to PIN entry
      setQrData(qrIdentifier);
      setStaffInfo(data);
      setAuthStep('pin');
    } catch (err) {
      setError('Failed to verify QR code. Please try again.');
      setAuthStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePINSubmit = async (pin) => {
    setLoading(true);
    setError(null);

    try {
      // Validate PIN and clock in
      const { data, error: pinError } = await staffAuthService?.clockInWithQRPIN(
        qrData,
        pin,
        staffInfo?.gym_id,
        'Main Entrance'
      );

      if (pinError || !data?.success) {
        setError(pinError?.message || 'Invalid PIN. Please try again.');
        setLoading(false);
        return;
      }

      // Success!
      setSuccessMessage(`Welcome back, ${data?.name}!`);
      setAuthStep('success');

      // Redirect to appropriate dashboard after 2 seconds
      setTimeout(() => {
        const redirectPath = roleRedirectMap?.[data?.role] || '/staff-dashboard';
        navigate(redirectPath);
      }, 2000);
    } catch (err) {
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAuthStep('qr');
    setQrData(null);
    setStaffInfo(null);
    setError(null);
    setSuccessMessage('');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Staff QR & PIN Login - TrainaryGym</title>
        <meta name="description" content="Secure staff authentication using QR code and PIN verification for workforce management access." />
      </Helmet>

      <div className="min-h-screen relative flex items-center justify-center p-4 md:p-6 lg:p-8 bg-background">
        <div className="relative z-10 w-full max-w-5xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                Staff Authentication
              </h1>
            </div>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Secure dual-factor authentication for staff, managers, and trainers
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Authentication Panel */}
            <div className="lg:col-span-2">
              <div className="card">
                {/* Progress Indicator */}
                <div className="mb-8">
                  <div className="flex items-center justify-center gap-4">
                    <div className={`flex items-center gap-2 ${
                      authStep === 'qr' ? 'text-primary' : 
                      authStep === 'pin' || authStep === 'success' ? 'text-success' : 'text-muted-foreground'
                    }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        authStep === 'qr' ? 'border-primary bg-primary/10' :
                        authStep === 'pin' || authStep === 'success' ? 'border-success bg-success/10' : 'border-border'
                      }`}>
                        {authStep === 'pin' || authStep === 'success' ? (
                          <Icon name="CheckCircle" size={20} />
                        ) : (
                          <span className="font-semibold">1</span>
                        )}
                      </div>
                      <span className="text-sm font-medium hidden sm:inline">Scan QR</span>
                    </div>

                    <div className="w-16 h-0.5 bg-border"></div>

                    <div className={`flex items-center gap-2 ${
                      authStep === 'pin' ? 'text-primary' : 
                      authStep === 'success' ? 'text-success' : 'text-muted-foreground'
                    }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        authStep === 'pin' ? 'border-primary bg-primary/10' :
                        authStep === 'success' ? 'border-success bg-success/10' : 'border-border'
                      }`}>
                        {authStep === 'success' ? (
                          <Icon name="CheckCircle" size={20} />
                        ) : (
                          <span className="font-semibold">2</span>
                        )}
                      </div>
                      <span className="text-sm font-medium hidden sm:inline">Enter PIN</span>
                    </div>

                    <div className="w-16 h-0.5 bg-border"></div>

                    <div className={`flex items-center gap-2 ${
                      authStep === 'success' ? 'text-success' : 'text-muted-foreground'
                    }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        authStep === 'success' ? 'border-success bg-success/10' : 'border-border'
                      }`}>
                        {authStep === 'success' ? (
                          <Icon name="CheckCircle" size={20} />
                        ) : (
                          <span className="font-semibold">3</span>
                        )}
                      </div>
                      <span className="text-sm font-medium hidden sm:inline">Complete</span>
                    </div>
                  </div>
                </div>

                {/* Authentication Steps */}
                {authStep === 'qr' && (
                  <QRScanner 
                    onScanSuccess={handleQRScan}
                    loading={loading}
                    error={error}
                  />
                )}

                {authStep === 'pin' && (
                  <PINEntry
                    staffInfo={staffInfo}
                    onPINSubmit={handlePINSubmit}
                    onBack={handleReset}
                    loading={loading}
                    error={error}
                  />
                )}

                {(authStep === 'success' || authStep === 'error') && (
                  <AuthenticationStatus
                    success={authStep === 'success'}
                    message={authStep === 'success' ? successMessage : error}
                    onReset={handleReset}
                  />
                )}

                {/* Back to Login */}
                <div className="mt-8 pt-6 border-t border-border text-center">
                  <button
                    onClick={handleBackToLogin}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-250 flex items-center justify-center gap-2 mx-auto"
                  >
                    <Icon name="ArrowLeft" size={16} />
                    <span>Back to Standard Login</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Security Features Sidebar */}
            <div className="lg:col-span-1">
              <SecurityFeatures />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffQRCodePINLogin;